/**
 * implement command — uses the built-in LLM Feature Implementer agent
 * to add LLM-powered commands to a CLI tool project.
 *
 * The agent is defined in DSL (dsl/implement-dsl.yaml) and its contracts
 * are pre-generated into src/builtin-agents/generated/.
 */

import { existsSync, readFileSync } from "node:fs";
import { resolve, join } from "node:path";
import { fileURLToPath } from "node:url";

import { runWorkflow } from "./workflow-runner.js";
import { buildTaskPrompt } from "./task-runner.js";
import type { SdkAdapter, TaskContext } from "./task-runner.js";
import type { WorkflowInvocation } from "./workflow-runner.js";
import {
  agentRegistry,
  taskRegistry,
  handoffSchemas,
  workflowRegistry,
  resolvedDsl,
} from "../builtin-agents/generated/index.js";
import {
  createGuardrailHooksFromRules,
  type GuardrailHooks,
  type GuardrailRuleData,
} from "./guardrail-hooks.js";
import type { ImplementationResult } from "../builtin-agents/generated/handoffs.js";

// ---------------------------------------------------------------------------
// Locale detection
// ---------------------------------------------------------------------------

function getLocale(): string {
  return (
    process.env.LANG?.split(".")[0] ??
    process.env.LC_ALL?.split(".")[0] ??
    process.env.LANGUAGE?.split(":")[0] ??
    "en_US"
  );
}

function isJapanese(): boolean {
  return getLocale().startsWith("ja");
}

// ---------------------------------------------------------------------------
// Messages — locale-aware
// ---------------------------------------------------------------------------

interface Messages {
  noProjectDir: (dir: string) => string;
  adapterError: (msg: string) => string;
  dryRunHeader: string;
  dryRunProjectDir: (dir: string) => string;
  dryRunDescription: (desc: string) => string;
  dryRunRefIncluded: string;
  dryRunRefNotFound: string;
  implementStarting: (adapter: string) => string;
  implementSuccess: string;
  implementFailed: (reason: string) => string;
  filesCreated: string;
  filesModified: string;
  commandsAdded: string;
  nextSteps: string;
}

const en: Messages = {
  noProjectDir: (dir) => `Project directory not found: ${dir}`,
  adapterError: (msg) => `Adapter initialization failed: ${msg}`,
  dryRunHeader: "=== Dry Run ===",
  dryRunProjectDir: (dir) => `Project: ${dir}`,
  dryRunDescription: (desc) => `Description: ${desc}`,
  dryRunRefIncluded: "Reference: integrate-llm-commands (included)",
  dryRunRefNotFound: "Reference: integrate-llm-commands (not found, will proceed without)",
  implementStarting: (adapter) => `Starting implementation with ${adapter} adapter...`,
  implementSuccess: "Implementation completed successfully.",
  implementFailed: (reason) => `Implementation failed: ${reason}`,
  filesCreated: "Files created:",
  filesModified: "Files modified:",
  commandsAdded: "Commands added:",
  nextSteps: "Next steps:",
};

const ja: Messages = {
  noProjectDir: (dir) => `プロジェクトディレクトリが見つかりません: ${dir}`,
  adapterError: (msg) => `アダプター初期化に失敗しました: ${msg}`,
  dryRunHeader: "=== ドライラン ===",
  dryRunProjectDir: (dir) => `プロジェクト: ${dir}`,
  dryRunDescription: (desc) => `説明: ${desc}`,
  dryRunRefIncluded: "リファレンス: integrate-llm-commands（含まれています）",
  dryRunRefNotFound: "リファレンス: integrate-llm-commands（未検出、リファレンスなしで続行）",
  implementStarting: (adapter) => `${adapter} アダプターで実装を開始します...`,
  implementSuccess: "実装が正常に完了しました。",
  implementFailed: (reason) => `実装に失敗しました: ${reason}`,
  filesCreated: "作成されたファイル:",
  filesModified: "変更されたファイル:",
  commandsAdded: "追加されたコマンド:",
  nextSteps: "次のステップ:",
};

function msg(): Messages {
  return isJapanese() ? ja : en;
}

// ---------------------------------------------------------------------------
// Reference document loader
// ---------------------------------------------------------------------------

function loadReferenceDoc(): string | undefined {
  const candidates = [
    // Shipped with the package (templates/ sibling of dist/)
    join(fileURLToPath(import.meta.url), "..", "..", "..", "templates", "implement-reference.md"),
    // Dev-time: from the skill directory
    join(process.env.HOME ?? "", ".cursor", "skills", "integrate-llm-commands", "SKILL.md"),
  ];

  for (const candidate of candidates) {
    try {
      if (existsSync(candidate)) {
        return readFileSync(candidate, "utf8");
      }
    } catch {
      // continue
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Adapter factory
// ---------------------------------------------------------------------------

async function createImplementAdapter(
  adapterName: string,
  projectDir: string,
  model?: string,
  guardrailHooks?: GuardrailHooks,
): Promise<SdkAdapter> {
  switch (adapterName) {
    case "claude": {
      const { ClaudeAgentSdkAdapter } = await import("../adapters/claude-agent-sdk.js");
      return new ClaudeAgentSdkAdapter({
        cwd: projectDir,
        permissionMode: "bypassPermissions",
        model,
        guardrailHooks,
      });
    }
    case "mock": {
      const { MockAdapter } = await import("../adapters/mock.js");
      return new MockAdapter();
    }
    default:
      throw new Error(
        `Unsupported adapter for implement: "${adapterName}". ` +
        `Available: claude, mock`,
      );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ImplementOptions {
  adapter: string;
  projectDir: string;
  dryRun: boolean;
  reportFormat: "json" | "text" | "yaml";
  model?: string;
  verbose?: boolean;
  progressOutput?: { write(chunk: string): unknown };
}

export interface ImplementRunResult {
  status: "success" | "failed" | "dry_run";
  exitCode: number;
  data?: ImplementationResult;
  error?: string;
}

export async function runImplement(
  description: string,
  options: ImplementOptions,
): Promise<ImplementRunResult> {
  const m = msg();
  const projectDir = resolve(options.projectDir);

  if (!existsSync(projectDir)) {
    return { status: "failed", exitCode: 3, error: m.noProjectDir(projectDir) };
  }

  const referenceDoc = loadReferenceDoc();

  // --- Build invocation context (shared between dry-run and live run) ---
  const userRequest = [
    description,
    "",
    `Project directory: ${projectDir}`,
  ].join("\n");

  const context: TaskContext = {
    user_request: userRequest,
    relevant_paths: [projectDir],
  };

  if (referenceDoc) {
    context.prior_context =
      "# Reference: integrate-llm-commands pattern\n\n" +
      "Follow this reference exactly when implementing LLM commands.\n\n" +
      referenceDoc;
  }

  // --- Dry run: emit the real prompt without calling the LLM ---
  if (options.dryRun) {
    const agent = agentRegistry["llm-feature-implementer"];
    const task = taskRegistry["implement-llm-feature"];
    const prompt = buildTaskPrompt(agent, task, context, { handoffSchemas });

    return {
      status: "dry_run",
      exitCode: 0,
      data: {
        files_created: [],
        files_modified: [],
        summary: prompt,
      },
    };
  }

  // --- Create adapter ---
  let guardrailHooks: GuardrailHooks | undefined;
  const rawRules = resolvedDsl._guardrailRules as GuardrailRuleData | undefined;
  if (
    rawRules &&
    (rawRules.commandRules?.length ||
      rawRules.fileRules?.length ||
      rawRules.contentRules?.length)
  ) {
    guardrailHooks = createGuardrailHooksFromRules(rawRules);
  }

  let adapter: SdkAdapter;
  try {
    adapter = await createImplementAdapter(
      options.adapter,
      projectDir,
      options.model,
      guardrailHooks,
    );
  } catch (err) {
    return { status: "failed", exitCode: 12, error: m.adapterError((err as Error).message) };
  }

  if (!options.verbose) {
    console.error(m.implementStarting(options.adapter));
  }

  const registries = {
    workflowRegistry,
    taskRegistry,
    agentRegistry,
    handoffSchemas,
  };

  // --- Run workflow ---
  const invocation: WorkflowInvocation = {
    workflow: "implement-llm-feature",
    user_request: userRequest,
    runtime: {
      maxFollowUps: 3,
      maxRetries: 1,
    },
    hooks: {
      onGate: async () => true,
    },
    context: {
      cwd: projectDir,
    },
    progressOutput: options.progressOutput,
  };

  const result = await runWorkflow(adapter, invocation, registries);

  if (result.status !== "completed") {
    const reason = result.escalation_reason ?? result.error_message ?? result.status;
    return { status: "failed", exitCode: 1, error: m.implementFailed(reason) };
  }

  // Extract structured result from the last successful step
  const lastStep = result.steps[result.steps.length - 1];
  const outcomeData = (lastStep?.outcome as Record<string, unknown>)?.data as
    | ImplementationResult
    | undefined;

  return {
    status: "success",
    exitCode: 0,
    data: outcomeData ?? {
      files_created: [],
      files_modified: [],
      summary: m.implementSuccess,
    },
  };
}

// ---------------------------------------------------------------------------
// Text formatter
// ---------------------------------------------------------------------------

export function formatImplementResult(result: ImplementRunResult): string {
  const m = msg();

  if (result.status === "dry_run") {
    return result.data?.summary ?? m.dryRunHeader;
  }

  if (result.status === "failed") {
    return result.error ?? m.implementFailed("unknown");
  }

  const data = result.data;
  if (!data) return m.implementSuccess;

  const lines: string[] = [data.summary, ""];

  if (data.files_created.length > 0) {
    lines.push(m.filesCreated);
    for (const f of data.files_created) lines.push(`  + ${f}`);
    lines.push("");
  }

  if (data.files_modified.length > 0) {
    lines.push(m.filesModified);
    for (const f of data.files_modified) lines.push(`  ~ ${f}`);
    lines.push("");
  }

  if (data.commands_added && data.commands_added.length > 0) {
    lines.push(m.commandsAdded);
    for (const c of data.commands_added) {
      lines.push(`  - ${c.name} [${c.type}]${c.description ? ` — ${c.description}` : ""}`);
    }
    lines.push("");
  }

  if (data.next_steps && data.next_steps.length > 0) {
    lines.push(m.nextSteps);
    for (const s of data.next_steps) lines.push(`  ${s}`);
  }

  return lines.join("\n");
}
